import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DateTime } from 'luxon';

import { KcalAdherenceTone, ProgressStatus } from '@cf-shared/body-comp-overview';
import { StatusChipTone } from 'components/cs-status-chip/cs-status-chip';
import { LL } from 'i18n/i18n.service';
import { BodyCompOverviewModel, OverviewIntakeDay } from 'services/body-comp-overview-model';
import { SettingsService } from 'services/settings.service';
import { formatShortDate } from 'util/formatting';

/**
 * The Body Composition overview cards (#5456): progress check, weight response, weight chart,
 * energy intake, skinfolds, waist and adherence.
 *
 * Presentational only. It draws a model that `BodyCompOverviewDataService` built; it loads
 * nothing and works nothing out. Both hosts (the Progress page and the weekly check-up) hand it
 * the same kind of model. Since #6676 it draws with the app's own pieces: `cs-status-chip` for a
 * verdict, the Progress page's measurement rows and arrows, and its chart style. See
 * design-docs/coaching-dashboard.md, "The cards".
 */
@Component({
  selector: 'cs-body-comp-overview',
  templateUrl: 'cs-body-comp-overview.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsBodyCompOverviewComponent {
  protected LL = LL;

  /** what to draw; undefined while the first load is running */
  @Input() model: BodyCompOverviewModel | undefined;
  @Input() loading = false;
  /** true when the load failed; the cards are replaced by a message with a Retry button */
  @Input() failed = false;
  @Output() retry = new EventEmitter<void>();

  // chart colours are read from the theme, the way the weekly check-up page reads its own
  private readonly rootStyles = getComputedStyle(document.documentElement);
  protected readonly weightColor = this.themeColor('--chart-purple');
  protected readonly waistColor = this.themeColor('--chart-turquoise', '--chart-green');

  constructor(private settings: SettingsService) {}

  /** true when a chart draws the running trend's direction colours (#6627), so the legend explains them */
  protected get hasTrendDirections(): boolean {
    const model = this.model;
    if (!model) return false;
    const charts = [model.weight?.chart, model.waist?.chart];
    return charts.some(chart => !!chart?.band?.some(row => row.direction !== undefined));
  }

  /** the verdict chip's colour: on track green, off track red, no verdict yet gray */
  protected statusTone(status: ProgressStatus): StatusChipTone {
    switch (status) {
      case ProgressStatus.OnTrack: return StatusChipTone.Success;
      case ProgressStatus.OffTrack: return StatusChipTone.Assertive;
      default: return StatusChipTone.Neutral;
    }
  }

  /** the calorie chip's colour under owner rule A: green on target, yellow off it, never red */
  protected adherenceTone(tone: KcalAdherenceTone): StatusChipTone {
    return tone === KcalAdherenceTone.OnTarget ? StatusChipTone.Success : StatusChipTone.Warning;
  }

  /** a tooltip date in the user's date format */
  protected formatDate = (dateIso: string): string => formatShortDate(DateTime.fromISO(dateIso), this.settings);

  /** the height of one intake bar, as a percentage of the tallest bar or target */
  protected barHeightPercent(day: OverviewIntakeDay, maxKcal: number): number {
    return maxKcal > 0 ? Math.min(100, Math.max(0, day.consumed / maxKcal * 100)) : 0;
  }

  /** where the target line sits, as a percentage from the bottom */
  protected targetLinePercent(targetKcal: number | undefined, maxKcal: number): number | undefined {
    return targetKcal !== undefined && maxKcal > 0 ? Math.min(100, targetKcal / maxKcal * 100) : undefined;
  }

  /** the value of a theme colour variable, or of the fallback variable when the first is not defined */
  private themeColor(variable: string, fallbackVariable?: string): string {
    const value = this.rootStyles.getPropertyValue(variable).trim();
    return value || (fallbackVariable ? this.rootStyles.getPropertyValue(fallbackVariable).trim() : '');
  }
}
